import { useState } from 'react';
import type { AddressesData, MyAccountAddress } from '../MyAccount.types';
import { postAccountAction } from '../shared/postAccountAction';
import { AddressCard } from './AddressCard';
import { AddressForm } from './AddressForm';

interface AddressesPageProps {
  data: AddressesData;
}

export const AddressesPage = ({ data }: AddressesPageProps) => {
  const [addresses, setAddresses] = useState(data.addresses);
  const [showForm, setShowForm] = useState(false);
  const [editingAddress, setEditingAddress] = useState<MyAccountAddress | null>(null);
  const [isPending, setIsPending] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const postAction = async (url: string, body: Record<string, unknown>) => {
    setIsPending(true);
    try {
      return await postAccountAction(url, body);
    } finally {
      setIsPending(false);
    }
  };

  const handleSetPreferred = async (addressId: string) => {
    const result = await postAction(data.endpoints.setPreferred, { addressId });
    if (result.addresses) setAddresses(result.addresses);
  };

  const handleRemove = async (addressId: string) => {
    const result = await postAction(data.endpoints.remove, { addressId });
    if (result.addresses) setAddresses(result.addresses);
  };

  const handleSave = async (address: Record<string, unknown>) => {
    setSaveError(null);
    const isEdit = !!address.addressId;
    const url = isEdit ? data.endpoints.update : data.endpoints.add;
    const result = await postAction(url, address);
    if (result.addresses) {
      setAddresses(result.addresses);
      setShowForm(false);
      setEditingAddress(null);
    } else {
      setSaveError(result.message ?? 'Unable to save address. Please try again.');
    }
  };

  const handleEdit = (address: MyAccountAddress) => {
    setEditingAddress(address);
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingAddress(null);
    setSaveError(null);
  };

  return (
    <>
      <div className="ma-head"><h3>Your addresses</h3></div>
      <p className="ma-hint">
        Tap the <strong>star</strong> on any address to make it your preferred delivery address.
      </p>

      {showForm ? (
        <AddressForm
          address={editingAddress}
          countries={data.countries}
          getStatesUrl={data.endpoints.getStates}
          onSave={handleSave}
          onCancel={handleCancel}
          isPending={isPending}
          serverError={saveError}
        />
      ) : (
        <div className="addr-grid">
          {addresses.map(addr => (
            <AddressCard
              key={addr.addressId}
              address={addr}
              onSetPreferred={() => handleSetPreferred(addr.addressId)}
              onEdit={() => handleEdit(addr)}
              onRemove={() => handleRemove(addr.addressId)}
            />
          ))}

          <button type="button" className="addr-add" onClick={() => setShowForm(true)}>
            <span className="addr-add-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            </span>
            Add a new address
          </button>
        </div>
      )}
    </>
  );
};
